import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { SITE } from "@/config/site";

export default function Navbar() {
  const { t, lang, toggleLang, isRTL } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/projects", label: t.nav.projects, key: "projects" },
    { to: "/clients", label: t.nav.clients, key: "clients" },
    { to: "/manpower", label: t.nav.manpower, key: "manpower" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b ${
        scrolled
          ? "bg-navy-800/92 backdrop-blur-xl py-3 border-white/5 shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
          : "bg-transparent py-5 border-transparent"
      }`}
    >
      <div className="container-pad flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-3 group"
          aria-label="Elite Al Hijaz home"
        >
          <span className="relative inline-flex">
            <img
              src={SITE.logoUrl}
              alt="Elite Al Hijaz Est."
              className="w-11 h-11 rounded-full object-cover ring-1 ring-gold-500/30 group-hover:ring-gold-500/70 transition"
            />
            <span className="absolute inset-0 rounded-full bg-gold-500/10 blur-md opacity-0 group-hover:opacity-100 transition" />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-extrabold text-white tracking-tight text-sm">
              {lang === "ar" ? "نخبة الحجاز" : "ELITE AL HIJAZ"}
            </span>
            <span className="text-[10px] text-overline -mt-0.5">
              {lang === "ar" ? "تصنيع · مقاولات" : "Steel · Contracting"}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${
                  isActive ? "text-gold-500" : "text-white/85 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{l.label}</span>
                  {isActive && (
                    <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-gradient-to-r from-transparent via-gold-500 to-transparent" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleLang}
            data-testid="navbar-language-toggle"
            className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-white/90 hover:text-gold-500 hover:border-gold-500/60 transition-colors text-xs font-bold tracking-wider"
          >
            <Languages className="w-4 h-4" />
            <span>{t.nav.switchLang}</span>
          </button>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/15 text-white"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="navbar-mobile-drawer"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container-pad pb-6 pt-2">
          <div className="glass-strong p-2">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.key}`}
                className={({ isActive }) =>
                  `block px-4 py-3 text-sm font-semibold ${isRTL ? "text-right" : "text-left"} ${
                    isActive ? "text-gold-500 bg-white/5" : "text-white/85 hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
